import { ResultOf } from "gql.tada";
import { InsightsHeroById } from "../../queries/entries/InsightsHeroById";
import { useTheme } from "../../hooks/useTheme";
import { useAnimate, useInView, useScroll, useTransform } from "motion/react";
import { useEffect, useRef } from "react";
import { usePaletteValues } from "../../hooks/usePaletteValues";
import { animationCurves } from "../../utils/animationCurves";
import { MediaItem } from "../shared/MediaItem";
import { DynamicScaling } from "../shared/DynamicScaling";
import { MarkdownLite } from "../shared/MarkdownLite";

export const InsightsHero = ({
  entry: { insightsHero: entry },
}: {
  entry: ResultOf<typeof InsightsHeroById>;
}) => {
  const sectionRef = useRef(null);

  const { theme, backgroundColor } = useTheme();
  const paletteValues = usePaletteValues(theme.palette);
  const [, animate] = useAnimate();

  const inView = useInView(sectionRef);
  const { scrollYProgress: sectionExitProgress } = useScroll({
    target: sectionRef,
    offset: ["end end", "end start"],
  });
  const backgroundTransitionBackToDefault = useTransform(
    sectionExitProgress,
    [0, 1],
    [paletteValues.deep(), paletteValues[theme.background]()]
  );

  useTransform(() => {
    const scroll = sectionExitProgress.get();
    if (scroll === 0 || scroll === 1) return;
    backgroundColor.set(backgroundTransitionBackToDefault.get());
  });

  useEffect(() => {
    backgroundColor.animation?.cancel();
    animate(
      backgroundColor,
      inView
        ? backgroundTransitionBackToDefault.get()
        : paletteValues[theme.background](),
      animationCurves.default
    );
  }, [
    animate,
    theme,
    backgroundColor,
    paletteValues,
    backgroundTransitionBackToDefault,
    inView,
  ]);

  return (
    <section
      ref={sectionRef}
      className="h-screen container-base pt-20 dsk:pt-16 pb-4"
    >
      <div className="size-full relative flex flex-col">
        {entry?.backgroundImage && (
          <MediaItem
            alt=""
            aria-hidden
            item={entry?.backgroundImage}
            className="absolute inset-0 size-full object-cover rounded-xl brightness-60"
          />
        )}
        <img />
        <div className="size-full z-10 flex flex-col justify-between items-center py-2">
          <DynamicScaling className="px-4">
            <h1 className="text-display-caps text-center py-4">
              {entry?.title}
            </h1>
          </DynamicScaling>
          <p className="text-body-regular max-w-[448px] text-center text-neutral-light py-6 px-7">
            <MarkdownLite>{entry?.subTitle}</MarkdownLite>
          </p>
        </div>
      </div>
    </section>
  );
};
